/**
 * Manejo global de errores de mutación — la red de seguridad del QueryClient.
 *
 * Incidente 2026-07-28: Jacqueline hacía clic en el basurero de un bono, el
 * backend devolvía 403 PERMISSION_DENIED y el botón simplemente no hacía nada.
 * La mutación no tenía `onError`, no había manejo global y el interceptor de
 * `apiClient` solo actúa sobre el 401, así que el error moría en el estado
 * interno de la mutación sin que nadie lo leyera.
 *
 * ⚠️ El aviso va DIFERIDO (150 ms) y se suprime si alguien ya mostró el error.
 * No es cosmético: los callbacks de `mutate(vars, { onError })` viven en un
 * WeakMap privado del observer de TanStack, así que desde acá no se puede saber
 * si el llamador maneja el error — la señal la dejan los manejos locales al
 * mostrar (ver erroresMostrados.ts). Sin el defer, los ~280 sitios que ya
 * manejan su error verían el mensaje dos veces.
 *
 * Vive acá y no inline en el queryClient para poder testearlo.
 */
import { getApiErrorMessage } from '@/lib/apiClient'
import { errorYaMostrado, huboToastDeErrorDesde } from '@/lib/erroresMostrados'
import { emitToast } from '@/lib/globalToast'

/** Lo mínimo que necesitamos de la mutación (evita atarnos al tipo de TanStack). */
interface MutationLike {
  options: { onError?: unknown }
}

/** Cuánto espera la red antes de avisar, para que el manejo local llegue primero. */
export const ESPERA_ANTES_DE_AVISAR_MS = 150

export function handleMutationError(error: unknown, mutation: MutationLike): void {
  // Si la mutación declara su onError a nivel de hook, no duplicamos el mensaje.
  if (mutation.options.onError) return

  // El 401 ya lo intercepta apiClient: cierra sesión y redirige al login.
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 401) return

  // `marcar: false`: la red corre ANTES que los callbacks locales (orden del
  // MutationCache), así que si esta extracción marcara el error, se suprimiría
  // a sí misma.
  const mensaje = getApiErrorMessage(error, { marcar: false })
  const desde = Date.now()

  setTimeout(() => {
    if (errorYaMostrado(error)) return
    if (huboToastDeErrorDesde(desde)) return
    emitToast('error', mensaje)
  }, ESPERA_ANTES_DE_AVISAR_MS)
}
